import type { ReactNode } from 'react';
import { Steps } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IOsasPatientQuestionnaireCustomControlData } from '../../../../../common/validation/customControls/patientQuestionnaire';
import type { ISchemaContainer } from '../../../../../common/validation/ISchemaContainer';
import type { ValidationErrors } from '../../../../utils/validation';
import { PatientDataError } from '../../../../../common/enums/PatientDataError';
import {
    osasPatientQuestionnaireCustomControlDataSchema,
    osasQuestionnaireStep1Schema,
    osasQuestionnaireStep2Schema,
    osasQuestionnaireStep3Schema,
    osasQuestionnaireStep4Schema,
} from '../../../../../common/validation/customControls/patientQuestionnaire';
import { fetchJson } from '../../../../store/fetch';
import { useViewportWidth } from '../../../../utils/hooks/useViewportWidth';
import { emptyValidationErrors, validate } from '../../../../utils/validation';
import { BackButton } from '../../../elements/atoms/backButton/BackButton';
import { Main } from '../../../elements/molecules/main/Main';
import { Footer } from '../../../elements/organisms/footer/Footer';
import { Header } from '../../../elements/organisms/header/Header';
import { PageWrapper } from '../../../elements/organisms/pageWrapper/PageWrapper';
import { QuestionnaireNotAvailable } from '../questionnaires/QuestionnaireNotAvailable';
import { Intro } from './steps/Intro';
import { QuestionnaireSent } from './steps/QuestionnaireSent';
import { Step1 } from './steps/Step1';
import { Step2 } from './steps/Step2';
import { Step3 } from './steps/Step3';
import { Step4 } from './steps/Step4';

export interface IStepProps {
    customControlData: IOsasPatientQuestionnaireCustomControlData;
    errors: ValidationErrors;
    goToPreviousStep: () => void;
    handleChange: (update: Partial<IOsasPatientQuestionnaireCustomControlData>) => void;
    isSubmitting?: boolean;
    submit?: () => void;
    tokenId?: string;
    validateAndGoToNextStep: () => Promise<void>;
}

export function OsasPatientQuestionnaire() {
    const navigate = useNavigate();
    const params = useParams();
    const tokenId = params.patientQuestionnaireId;

    const [currentStep, setCurrentStep] = useState<number>(0);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [areValidationsActivated, setAreValidationsActivated] = useState<boolean>(false);
    const [isInvitationValid, setIsInvitationValid] = useState<boolean | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [customControlData, setCustomControlData] = useState<IOsasPatientQuestionnaireCustomControlData>({
        doneBetter: null,
        enoughInformation: null,
        explanationClear: null,
        explanationUnderstandable: null,
        feltHelped: null,
        opportunityToAskQuestions: null,
        repeatDigitalExamination: null,
        satisfiedWith: null,
        scoreFreeText: null,
        scoreKsyosSleep: null,
        sleepExaminationFreeText: null,
        videoConsultFreeText: null,
    });

    const { width } = useViewportWidth();

    useEffect(() => {
        if (areValidationsActivated) {
            validateOnChange();
        }
    }, [customControlData]);

    useEffect(() => {
        getPatientQuestionnaire();
    }, []);

    async function getPatientQuestionnaire() {
        try {
            const url = `/api/patient/patientQuestionnaire/${tokenId}/status`;
            const result = await fetchJson(url, 'GET');

            if (result.json.isActive) {
                setIsInvitationValid(true);
            } else {
                setIsInvitationValid(false);
            }
        } catch {
            navigate(`/saml/login?error=${PatientDataError.invalidUrl}`);
        }
    }

    function getValidationSchemaByStep(step: number) {
        switch (step) {
            case 0:
                return null;
            case 1:
                return osasQuestionnaireStep1Schema;
            case 2:
                return osasQuestionnaireStep2Schema;
            case 3:
                return osasQuestionnaireStep3Schema;
            case 4:
                return osasQuestionnaireStep4Schema;
            case 5:
                return null;
            default:
                throw new Error('Schema not defined for step ' + step);
        }
    }

    async function validateAndGoToNextStep() {
        setAreValidationsActivated(true);
        const schema = getValidationSchemaByStep(currentStep);
        if (schema) {
            const validationResult = getValidationResult(schema);
            if (validationResult.errors.size === 0) {
                goToNextStep();
            } else {
                setErrors(validationResult.errors);
            }
        } else {
            goToNextStep();
        }
    }

    function validateOnChange() {
        const schema = getValidationSchemaByStep(currentStep);
        if (schema) {
            const validationResult = getValidationResult(schema);
            setErrors(validationResult.errors);
        }
    }

    function getValidationResult(schema: ISchemaContainer<unknown>) {
        return validate(schema, customControlData);
    }

    function goToNextStep() {
        setAreValidationsActivated(false);
        setErrors(emptyValidationErrors);
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    }

    function handleChange(update: Partial<IOsasPatientQuestionnaireCustomControlData>) {
        setCustomControlData({ ...customControlData, ...update });
    }

    async function submitPatientQuestionnaire() {
        setIsSubmitting(true);
        const validationResult = validate(osasPatientQuestionnaireCustomControlDataSchema, customControlData);
        if (validationResult.errors.size === 0) {
            await fetchJson(`/api/patient/patientQuestionnaire/${tokenId}`, 'POST', { customControlData });
            goToNextStep();
        } else {
            setIsSubmitting(false);
        }
    }

    function goToPreviousStep() {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    }

    const stepProps = {
        customControlData,
        errors,
        goToPreviousStep,
        handleChange,
        procedure: customControlData,
        tokenId,
        validateAndGoToNextStep,
    };

    const steps: ReactNode[] = [
        <Intro
            key={0}
            {...stepProps}
        />,
        <Step1
            key={1}
            {...stepProps}
        />,
        <Step2
            key={2}
            {...stepProps}
        />,
        <Step3
            key={3}
            {...stepProps}
        />,
        <Step4
            key={4}
            {...stepProps}
            isSubmitting={isSubmitting}
            submit={submitPatientQuestionnaire}
        />,
        <QuestionnaireSent key={5} />,
    ];

    if (isInvitationValid === null) {
        return null;
    }

    if (!isInvitationValid) {
        return <QuestionnaireNotAvailable />;
    }

    const backButton = <BackButton goBackFunction={goToPreviousStep} />;

    return (
        <PageWrapper
            fullHeight
            scrollToTopUpdater={currentStep}
        >
            <Header
                customBackButton={backButton}
                disableLogoLink={true}
                hasBackbutton={currentStep > 0 && currentStep < 5 && !isSubmitting}
            />
            <Main backgroundColor="#f5f5f4">
                {currentStep > 0 && currentStep < 5 && (
                    <Steps
                        activeStep={currentStep - 1}
                        style={{ margin: '40px 0px' }}
                    >
                        <Steps.Step label={width > 768 ? 'Slaaponderzoek' : undefined} />
                        <Steps.Step label={width > 768 ? 'videoconsult met de arts' : undefined} />
                        <Steps.Step label={width > 768 ? 'rapportcijfer' : undefined} />
                        <Steps.Step label={width > 768 ? 'suggesties en/of complimenten' : undefined} />
                    </Steps>
                )}
                {currentStep < steps.length && steps[currentStep]}
            </Main>
            <Footer />
        </PageWrapper>
    );
}
